import { Observable } from 'rxjs';
import { ChatPayload } from '../interfaces/chart-details.interface';
import { Injectable } from '@angular/core';
import { TokenService } from 'src/app/shared/services/customer-api/token.service';
import { ConfigService } from 'src/app/service/config.service';

@Injectable({
  providedIn: 'root',
})
export class ChatStreamPostService {
  constructor(
    private tokenService: TokenService,
    private config: ConfigService,
  ) {}

  manualStreamChat(chatPayload: ChatPayload): Observable<{
    type: 'text' | 'json';
    data: string | Record<string, unknown>;
  }> {
    const requestUrl = `${this.config.appConfig.urlChatLLMApi}/stream/chat`;

    return new Observable<{
      type: 'text' | 'json';
      data: string | Record<string, unknown>;
    }>((observer) => {
      const abortController = new AbortController();
      const signal = abortController.signal;

      const timeoutId = setTimeout(() => {
        console.log('Cancelling request after 100 seconds timeout...');
        abortController.abort();
      }, 100000);

      fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + this.tokenService.getActiveToken(),
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
        body: JSON.stringify(chatPayload),
        signal: signal,
      })
        .then(async (response) => {
          const reader = response.body?.getReader();
          const decoder = new TextDecoder();

          try {
            let buffer = '';

            while (true) {
              const { done, value } = await reader!.read();
              if (done) break;

              const chunk = decoder.decode(value, { stream: true });
              buffer += chunk;

              const lines = buffer.split('\n');
              buffer = lines.pop() || '';

              lines.forEach((line) => {
                if (line.trim()) {
                  if (line.startsWith('event:')) {
                    const eventData = line.replace('event:', '').trim();
                    if (eventData) {
                      observer.next({ type: 'text', data: eventData });
                    }
                  } else if (line.startsWith('final:')) {
                    const jsonString = line.replace('final:', '').trim();
                    if (jsonString) {
                      try {
                        const parsed = JSON.parse(jsonString);

                        observer.next({ type: 'json', data: parsed });
                      } catch (parseError) {
                        console.error(
                          'Failed to parse final JSON:',
                          parseError,
                        );
                        console.error('JSON string was:', jsonString);
                        observer.error(parseError);
                      }
                    }
                  }
                }
              });
            }

            if (buffer.trim()) {
              const line = buffer.trim();
              if (line.startsWith('event:')) {
                const eventData = line.replace('event:', '').trim();
                if (eventData) {
                  observer.next({ type: 'text', data: eventData });
                }
              } else if (line.startsWith('final:')) {
                const jsonString = line.replace('final:', '').trim();
                if (jsonString) {
                  try {
                    const parsed = JSON.parse(jsonString);

                    observer.next({ type: 'json', data: parsed });
                  } catch (parseError) {
                    console.error(
                      'Failed to parse final JSON from buffer:',
                      parseError,
                    );
                    console.error('JSON string was:', jsonString);
                    observer.error(parseError);
                  }
                }
              }
            }

            observer.complete();
          } catch (error) {
            if (error.name === 'AbortError') {
              console.log('Request was cancelled');
              observer.complete();
            } else {
              console.error('Error reading stream:', error);
              observer.error(error);
            }
          } finally {
            clearTimeout(timeoutId);
            if (reader) {
              reader.releaseLock();
            }
          }
        })
        .catch((error) => {
          if (error.name === 'AbortError') {
            console.log('Request was cancelled');
            observer.complete();
          } else {
            console.error('Fetch error:', error);
            observer.error(error);
          }
          clearTimeout(timeoutId);
        });

      return () => {
        abortController.abort();
        clearTimeout(timeoutId);
      };
    });
  }
}
